import React, { useState } from "react";
import api from "@/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TestTubes } from "lucide-react";
import { apiErrorMessage } from "@/utils/apiError";
import { TRI_STATE_OPTIONS, triStateValue } from "@/utils/triState";
import { SAMPLE_FIELDS, hasSamples, shownSamples, takesSamples } from "@/utils/samples";

const statusOf = (trial, flag) => TRI_STATE_OPTIONS.find((o) => o.value === triStateValue(trial?.[flag]))?.label ?? "No";
const asText = (value) => (value === null || value === undefined ? "" : String(value));

/**
 * "Set samples": the number of NIT, Germination Test and TGW samples, each beside its activity's status. A number
 * can only be set while the activity is Yes; for No or To be decided the field is off and empty (a number set
 * before is kept, not shown). Saved with POST /trials/{id}/samples (only these numbers); `onSaved` gets them.
 * Shown on task cards, in Trial Details and on the activities' tasks when an activity is Yes.
 */
export function SamplesDialog({ trial, onSaved, size, className }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({});
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!hasSamples(trial)) return null;

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) {
      setValues(Object.fromEntries(SAMPLE_FIELDS.map(({ field }) => [field, asText(shownSamples(trial, field))])));
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const changes = {};
    for (const { field, flag, label } of SAMPLE_FIELDS) {
      if (!takesSamples(trial, flag) || values[field] === asText(trial[field])) continue;
      const text = values[field].trim();
      if (text !== "" && !/^\d+$/.test(text)) {
        setError(`${label} must be a whole number of 0 or more.`);
        return;
      }
      changes[field] = text === "" ? null : Number(text);
    }
    if (Object.keys(changes).length === 0) {
      setOpen(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await api.post(`/trials/${trial.id}/samples`, changes);
      setOpen(false);
      if (onSaved) onSaved(res.data);
    } catch (err) {
      setError(apiErrorMessage(err, "Failed to save the samples."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} modal={false}>
      <DialogTrigger asChild>
        <Button variant="outline" size={size} className={className} aria-label="Set samples" title="Set samples">
          <TestTubes />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Set samples</DialogTitle>
          <DialogDescription>
            The number of samples of each activity. It can only be set while the activity is Yes, not when it is No or To be decided.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          {SAMPLE_FIELDS.map(({ field, flag, label }) => {
            const enabled = takesSamples(trial, flag);
            return (
              <div key={field} className="grid grid-cols-[1fr_7rem] items-center gap-3">
                <div>
                  <Label htmlFor={`samples-${field}`}>{label}</Label>
                  <div className="text-xs text-gray-500 dark:text-gray-400" data-testid={`samples-status-${field}`}>
                    {statusOf(trial, flag)}
                  </div>
                </div>
                <Input
                  id={`samples-${field}`}
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  disabled={!enabled}
                  value={values[field] ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, [field]: e.target.value }))}
                />
              </div>
            );
          })}
          {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={handleSubmit} disabled={saving}>Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
